import { queryOptions } from '@tanstack/react-query'

import { fetchJson } from '@/api/client'
import { keys } from '@/api/keys'
import { Settings, type SettingsPatch } from '@/schema'

/**
 * `/api/settings` (§5.10), the AI test ping (§5.0) and the media cache
 * stats/sweep (§5.5) that the Settings page shows next to them. Secrets are
 * never part of the response.
 */

export async function getSettings(signal?: AbortSignal): Promise<Settings> {
  const raw = await fetchJson<unknown>('/api/settings', { signal })
  const parsed = Settings.safeParse(raw)
  if (parsed.success) return parsed.data
  // First start before the story seeds landed can leave `story.voices` short
  // of a role; keep rendering with the server's shape and say so once.
  console.warn('[settings] response failed contract validation', parsed.error.issues.slice(0, 5))
  return raw as Settings
}

export function patchSettings(patch: SettingsPatch): Promise<Settings> {
  return fetchJson<Settings>('/api/settings', { method: 'PATCH', body: patch })
}

export interface AiTestResult {
  ok: boolean
  cost_usd: number
  duration_ms: number
  model: string
}

/** One real request through the configured AI route; `503 ai_unavailable` on failure. */
export function testAi(): Promise<AiTestResult> {
  return fetchJson<AiTestResult>('/api/health/ai/test', { method: 'POST' })
}

export interface AntigravityModels {
  items: Array<{ id: string; label: string }>
  selected_model: string
}

export const agyModelsQuery = queryOptions({
  queryKey: keys.agyModels(),
  queryFn: ({ signal }) => fetchJson<AntigravityModels>('/api/ai/agy/models', { signal }),
  staleTime: 60_000,
  retry: false,
})

export const geminiModelsQuery = queryOptions({
  queryKey: keys.geminiModels(),
  queryFn: ({ signal }) => fetchJson<AntigravityModels>('/api/ai/gemini/models', { signal }),
  staleTime: 60_000,
  retry: false,
})

export function testAntigravity(): Promise<AiTestResult> {
  return fetchJson<AiTestResult>('/api/ai/agy/test', { method: 'POST' })
}

export interface OpenRouterTestResult {
  ok: boolean
  models: string[]
  cost_usd: number | null
  duration_ms: number
}

export function testOpenRouter(draft: { model: string; writing_model: string | null; api_key?: string }): Promise<OpenRouterTestResult> {
  return fetchJson<OpenRouterTestResult>('/api/ai/openrouter/test', { method: 'POST', body: draft })
}

export const openrouterModelsQuery = queryOptions({
  queryKey: keys.openrouterModels(),
  queryFn: ({ signal }) => fetchJson<{ items: Array<{ id: string; label: string; vision: boolean; structured_outputs: boolean }> }>('/api/ai/openrouter/models', { signal }),
  staleTime: 300_000,
  retry: false,
})

export interface CacheStats {
  bytes: number
  max_bytes: number
  entries: number
  pinned_bytes: number
  free_bytes: number
}

export function getCacheStats(signal?: AbortSignal): Promise<CacheStats> {
  return fetchJson<CacheStats>('/api/media/cache/stats', { signal })
}

export function sweepCache(): Promise<CacheStats> {
  return fetchJson<CacheStats>('/api/media/cache/sweep', { method: 'POST' })
}

/* ------------------------------------------------------------- queries --- */

export const settingsQuery = queryOptions({
  queryKey: keys.settings(),
  queryFn: ({ signal }) => getSettings(signal),
  staleTime: 30_000,
})

export const cacheStatsQuery = queryOptions({
  queryKey: keys.media.cacheStats(),
  queryFn: ({ signal }) => getCacheStats(signal),
  staleTime: 15_000,
  retry: 1,
})
